// Screen state (filters + date) ⇄ URL query params so a view can be
// bookmarked or shared: ?date=2026-07-14&offices=Bristol,Cardiff&types=rehash
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

type FilterValues = Record<string, string[]>;

const serialize = (params: URLSearchParams): string => {
  const query = params.toString().replace(/%2C/g, ",");
  return query ? `?${query}` : "";
};

// Values not in the allowed lists (renamed office, stale bookmark) are dropped
// rather than silently filtering every appointment out.
export const filtersFromSearch = (search: string, allowed: FilterValues): FilterValues => {
  const params = new URLSearchParams(search);

  return Object.keys(allowed).reduce<FilterValues>((filters, dimension) => {
    const values = (params.get(dimension) || "").split(",").filter(Boolean);
    filters[dimension] = values.filter((value) => (allowed[dimension] ?? []).includes(value));
    return filters;
  }, {});
};

export const searchWithFilters = (search: string, filters: FilterValues): string => {
  const params = new URLSearchParams(search);

  Object.keys(filters).forEach((dimension) => {
    const values = filters[dimension] ?? [];
    if (values.length) {
      params.set(dimension, values.join(","));
    } else {
      params.delete(dimension);
    }
  });

  return serialize(params);
};

export const dateFromSearch = (search: string, fallback: string): string => {
  const value = new URLSearchParams(search).get("date");
  const parsed = new Date(`${value}T00:00:00Z`);
  const valid =
    DATE_PATTERN.test(value ?? "") && !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value;

  return valid ? (value as string) : fallback;
};

// The date param is only present when it differs from the page's default
// (today), so a plain bookmark stays evergreen instead of pinning a past day.
export const searchWithDate = (search: string, date: string, defaultDate: string): string => {
  const params = new URLSearchParams(search);

  if (date === defaultDate) {
    params.delete("date");
  } else {
    params.set("date", date);
  }

  return serialize(params);
};
